import { useEffect, useState } from "react";
import { OfflineItemService } from "../../../../../services/api/OfflineOrder/offline.service";
import { useAuth } from "../../../../../context/AuthContext";
import dandarLogo from "../../../../../assets/images/dandar-logo.png";
import { SelectedProduct } from "./types";

interface OfflineBillProps {
  formData: {
    patientName?: string;
    patientMobile?: string;
    patientAddress?: string;
    patientPincode?: string;
    drName?: string;
    mobileNumber?: string;
    clinicName?:string;
    dlno?: string;
    address?: string;
    pincode?: string;
    paymentType?: string;
    registrationNumber?: string;
  }
  selectedProducts: SelectedProduct[];
  extraDiscount: number;
  coinsUsed?: number|"";
}

const OfflineBill:React.FC<OfflineBillProps> = ({
  formData = {},
  selectedProducts = [],
  extraDiscount,
  coinsUsed,
}) => {
  console.log("selectedProducts", selectedProducts);
  const [gstInNumber, setGSTINnumber] = useState({});
  const [clinicDetails, setClinicDetails] = useState({});
  const [, setError] = useState<null|string>(null);
  const { user } = useAuth();
  const clinicId = user?.role === "clinic" ? user?.id : "";

  useEffect(() => {
    const fetchClinicDetails = async () => {
      try {
        if (!clinicId) return;

        const response = await OfflineItemService.getClinicDetailsById(
          clinicId
        );
        const clinicData = response.data[0] || {};
        setClinicDetails(clinicData);
        if (clinicData.pincode) {
          await getClinicGSTNumber(clinicData.pincode);
        }
      } catch (err) {
        console.error("Failed to load clinic details:", err);
      }
    };

    fetchClinicDetails();
  }, [clinicId]);

  const getClinicGSTNumber = async (pincode:string) => {
    try {
      const response = await OfflineItemService.getClinicGSTNByPincode(pincode);
      setGSTINnumber(response);
    } catch (error) {
      console.log("Failed to fetch GSTN:", error);
      setError("Failed to fetch GSTN information");
    }
  };

  const calculateMRPTotal = () => {
    let mrpTotal = 0;
    let discountTotal = 0;

    if (!Array.isArray(selectedProducts)) {
      return {
        mrpTotal: "0.00",
        discountTotal: "0.00",
        grandTotal: "0.00",
      };
    }

    selectedProducts.forEach((product) => {
      mrpTotal += (product.perPackPrice || 0) * (product.selectedQuantity || 0);
      discountTotal += product.sellingDiscount
        ? (product.perPackPrice || 0) *
          (product.sellingDiscount / 100) *
          (product.selectedQuantity || 0)
        : 0;
    });

    const totalAmount=mrpTotal - discountTotal
    const decimalPart = totalAmount - Math.floor(totalAmount);
    let roundedAmount;

    if (decimalPart >= 0.5) {
      roundedAmount = Math.ceil(totalAmount);
    } else {
      roundedAmount = Math.floor(totalAmount);
    }
    const roundOffValue = parseFloat((roundedAmount - totalAmount).toFixed(2));


    return {
      mrpTotal: mrpTotal.toFixed(2),
      discountTotal: discountTotal.toFixed(2),
      roundOff: roundOffValue.toFixed(2),
      grandTotal: (roundedAmount - Number(coinsUsed || 0)).toFixed(2),
    };
  };

  const { mrpTotal, discountTotal, grandTotal,roundOff } = calculateMRPTotal();

  const calculateTaxBreakdown = () => {
    const taxRates = {};
    
    if (!Array.isArray(selectedProducts)) {
      return {};
    }

    selectedProducts.forEach((product) => {
      const cgstRate = product.cgst || 0;
      const sgstRate = product.sgst || 0;
      const totalTaxRate = cgstRate + sgstRate;
      
      if (!taxRates[totalTaxRate]) {
        taxRates[totalTaxRate] = {
          cgst: cgstRate,
          sgst: sgstRate,
          amount: 0,
        };
      }
      
      const productAmount = (product.perPackPrice || 0) * (product.selectedQuantity || 0);
      const discountedAmount = productAmount * (1 - (product.sellingDiscount || 0) / 100);
      const taxAmount = discountedAmount * (totalTaxRate / 100);
      
      taxRates[totalTaxRate].amount += taxAmount;
    });

    return taxRates;
  };

  const taxBreakdown = calculateTaxBreakdown();

  // Format the tax breakdown for display
  const formatTaxBreakdown = () => {
    if (Object.keys(taxBreakdown).length === 0) {
      return "No tax applicable";
    }

    return Object.entries(taxBreakdown)
      .map(([totalRate, { cgst, sgst, amount }]) => {
        return `${cgst}%CGST + ${sgst}%SGST = ${totalRate}% (₹${amount.toFixed(2)})`;
      })
      .join(", ");
  };

  const formatDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, "0");
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const year = today.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatTime = () => {
    const today = new Date();
    return today.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDayName = () => {
    const today = new Date();
    return today.toLocaleDateString("en-GB", { weekday: "long" });
  };

  function numberToWordsIndianWithPaise(amount) {
    const single = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
    ];
    const double = [
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];
    const formatTenth = (digit, prev) => {
      return 0 == digit ? "" : " " + (1 == digit ? double[prev] : tens[digit]);
    };
    const formatOther = (digit, next, denom) => {
      return (
        (0 != digit && 1 != next ? " " + single[digit] : "") +
        (0 != next || digit > 0 ? " " + denom : "")
      );
    };

    let str = "";
    let rupees = Math.floor(amount);
    let paise = Math.round((amount - rupees) * 100);

    if (rupees > 0) {
      const crore = Math.floor(rupees / 10000000) % 100;
      const lakh = Math.floor(rupees / 100000) % 100;
      const thousand = Math.floor(rupees / 1000) % 100;
      const hundred = Math.floor(rupees / 100) % 10;
      const ten = Math.floor(rupees / 10) % 10;
      const one = Math.floor(rupees) % 10;

      str += crore > 0 ? formatOther(crore, 0, "Crore") : "";
      str += lakh > 0 ? formatOther(lakh, 0, "Lakh") : "";
      str += thousand > 0 ? formatOther(thousand, 0, "Thousand") : "";
      str += hundred > 0 ? formatOther(hundred, 0, "Hundred") : "";

      if (ten > 0 || one > 0) {
        str += ten > 0 ? formatTenth(ten, one) : " " + single[one];
      }

      str += " Rupees";
    }

    if (paise > 0) {
      const ten = Math.floor(paise / 10) % 10;
      const one = Math.floor(paise) % 10;

      if (str.length > 0) {
        str += " and";
      }

      str += ten > 0 ? formatTenth(ten, one) : " " + single[one];
      str += " Paise";
    }

    return str + " Only";
  }

  const calculateStripAndTabletCounts = (selectedQuantity: number, noOfItemsInPack: number) => {
    const stripCount = Math.floor(selectedQuantity / noOfItemsInPack);
    const tabletCount = selectedQuantity % noOfItemsInPack;
    return { stripCount, tabletCount };
  };


  return (
    <div className="max-w-4xl mx-auto p-4 bg-white">
      <div className="border-2 border-black">
        {/* Header */}
        <div className="border-b-2 border-black p-3">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <img src={dandarLogo} alt="Dandar Logo" className="h-12" />
            </div>
            <div className="text-right text-sm">
              
              <div>
                Patient Name:{" "}
                <span className="">
                  {formData.patientName || "-"}
                </span>
              </div>
              <div>Patient Address: {formData.patientAddress || "-"}</div>
              <div>Patient Pincode: {formData.patientPincode || "-"}</div>
              <div>
                Dr Name:{" "}
                <span className="">{formData.drName || "-"}</span>
              </div>
            </div>
          </div>

          <div className="mt-2 text-sm">
            <div className="font-bold">{clinicDetails.name || "-"}</div>
            <div className="font-bold">
              {clinicDetails.address || formData.address || "-"}
            </div>
            <div>
              Phone:{" "}
              {clinicDetails.mobileNumber || formData.mobileNumber || "-"}
            </div>
          </div>
        </div>

        {/* GST and Invoice Details */}
        <div className="border-b-2 border-black p-3">
          <div className="flex justify-between">
            <div className="text-sm">
              <div>
                <strong>GSTIN:</strong> {gstInNumber?.gst_in || "-"}
              </div>
              <div>
                <strong>D.L.No.:</strong>{" "}
                {clinicDetails.drugLicenseNumber ||
                  formData.drugLicenseNumber ||
                  "-"}
              </div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold">GST INVOICE</div>
            </div>
            <div className="text-sm text-right">
              <div>
                <strong>Time:</strong> {formatTime()}
              </div>
              <div>
                <strong>Day:</strong> {getDayName()}
              </div>
            </div>
          </div>
        </div>

        {/* Product Table */}
        <div className="border-b-2 border-black">
          <table className="w-full text-sm">
            <thead className="border-b border-black">
              <tr>
                <th className="border-r border-black p-2 text-left">SN.</th>
                <th className="border-r border-black p-2 text-left">
                  PRODUCT NAME
                </th>
                <th className="border-r border-black p-2 text-left">PACK</th>
                <th className="border-r border-black p-2 text-left">HSN</th>
                <th className="border-r border-black p-2 text-left">BATCH</th>
                <th className="border-r border-black p-2 text-left">EXPIRY</th>
                <th className="border-r border-black p-2 text-left">STRIPS</th>
                <th className="border-r border-black p-2 text-left">TABLETS</th>
                <th className="border-r border-black p-2 text-left">MRP</th>
                <th className="border-r border-black p-2 text-left">DIS%</th>
                <th className="border-r border-black p-2 text-left">CGST</th>
                <th className="border-r border-black p-2 text-left">SGST</th>
                <th className="p-2 text-left">AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(selectedProducts) &&
                selectedProducts.map((product, index) => (
                  <tr key={index} className="border-b border-gray-300">
                    <td className="border-r border-black p-2">{index + 1}.</td>
                    <td className="border-r border-black p-2 font-medium">
                      {product.product_name || "N/A"}
                    </td>
                    <td className="border-r border-black p-2">
                      {product.noOfItemsInPack > 1? `1 * ${product.noOfItemsInPack}` : "1 * PIECE"}
                    </td>
                    <td className="border-r border-black p-2">
                      {product.hsn_number || "N/A"}
                    </td>
                    <td className="border-r border-black p-2">
                      {product.batch || "N/A"}
                    </td>
                    <td className="border-r border-black p-2">
                      {product.expiry
                        ? (() => {
                            const date = new Date(product.expiry);
                            const month = String(date.getMonth() + 1).padStart(
                              2,
                              "0"
                            ); // Months are 0-indexed
                            const year = String(date.getFullYear()).slice(-2); // Get last 2 digits of year
                            return `${month}/${year}`;
                          })()
                        : "N/A"}
                    </td>
                    <td className="border-r border-black p-2 text-center">
                      { Math.floor(product.selectedQuantity / product.noOfItemsInPack)}
                    </td>
                    <td className="border-r border-black p-2 text-center">
                      {product.selectedQuantity % product.noOfItemsInPack}
                    </td>
                    <td className="border-r border-black p-2 text-right">
                      {(product.mrp || 0).toFixed(2)}
                    </td>
                    <td className="border-r border-black p-2 text-right">
                      {product.sellingDiscount || 0}
                    </td>
                    <td className="border-r border-black p-2 text-center">
                      {product.cgst || 0}
                    </td>
                    <td className="border-r border-black p-2 text-center">
                      {product.sgst || 0}
                    </td>
                    <td className="p-2 text-right">
                      {(product.sellingDiscount
                        ? (product.perPackPrice || 0) *
                          (1 - product.sellingDiscount / 100) *
                          (product.selectedQuantity || 0)
                        : (product.perPackPrice || 0) * (product.selectedQuantity || 0)
                      ).toFixed(2)}
                    </td>
                  </tr>
                ))}
              {/* Empty rows to match original format */}
              {Array.from({
                length: Math.max(0, 6 - (selectedProducts?.length || 0)),
              }).map((_, index) => (
                <tr key={`empty-${index}`} className="border-b border-gray-300">
                  <td className="border-r border-black p-2 h-8">&nbsp;</td>
                  <td className="border-r border-black p-2">&nbsp;</td>
                  <td className="border-r border-black p-2">&nbsp;</td>
                  <td className="border-r border-black p-2">&nbsp;</td>
                  <td className="border-r border-black p-2">&nbsp;</td>
                  <td className="border-r border-black p-2">&nbsp;</td>
                  <td className="border-r border-black p-2">&nbsp;</td>
                  <td className="border-r border-black p-2">&nbsp;</td>
                  <td className="border-r border-black p-2">&nbsp;</td>
                  <td className="border-r border-black p-2">&nbsp;</td>
                  <td className="border-r border-black p-2">&nbsp;</td>
                  <td className="border-r border-black p-2">&nbsp;</td>
                  <td className="p-2">&nbsp;</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Tax Details */}
        <div className="border-b-2 border-black p-2 text-xs">
          <div>
            {formatTaxBreakdown()}
          </div>
        </div>

        {/* Footer */}
        <div className="px-3">
          <div className="flex justify-between w-full">
            <div className="text-sm w-[45%]">
              <div className="font-bold">Terms & Conditions</div>
              <div>Goods once sold will not be taken back.</div>
              <div>Please check medicines before leaving the counter.</div>
              <div>All disputes subject to PALAMU Jurisdiction only.</div>
              <div>NO CASH REFUND.</div>
            </div>
            <div className="flex justify-end items-end pb-5 w-[33%]">
              <div className="text-center text-xs pr-6 ">
                For DANDAR PHARMACEUTICALS PVT. LTD.
              </div>
            </div>

            <div className="flex flex-col items-center justify-center w-[22%]">
              <div className="mb-4 text-sm">
                <div className="flex justify-between w-48">
                  <span>SUB TOTAL</span>
                  <span>{mrpTotal}</span>
                </div>
                <div className="flex justify-between w-48">
                  <span>Discount</span>
                  <span>{discountTotal}</span>
                </div>
                <div className="flex justify-between w-48">
                  <span>Extra Discount</span>
                  <span>{extraDiscount}</span>
                </div>
                <div className="flex justify-between w-48">
                  <span>Coins Used</span>
                  <span>{coinsUsed}</span>
                </div>
                <div className="flex justify-between w-48">
                  <span>Round Off</span>
                  <span>{roundOff}</span>
                </div>
                <div className="flex justify-between w-48">
                  <span>PAYMENT TYPE </span>
                  <span className="">{formData.paymentType}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex mt-4 justify-between w-full">
            <div className=" text-sm font-bold w-[43%]">
              Rs. {numberToWordsIndianWithPaise(parseFloat(grandTotal) || 0)}
            </div>
            <div className="mb-2 text-right w-[33%]">
              <span className="text-xs px-6">Authorised Signatory</span>
            </div>
            <div className="border-t-2  border-black pt-2 w-[22%]">
              <div className="text-xs">
                <span>GRAND TOTAL</span>
                <span className="ml-8">
                  {(Number(grandTotal) - extraDiscount).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfflineBill;
